"use client"

import * as React from "react"
import { CheckIcon, ChevronDownIcon } from "lucide-react"
import { format, isFirstDayOfMonth, isLastDayOfMonth } from "date-fns"
import { toZonedTime } from "date-fns-tz"

import { Button } from "./button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./dropdown-menu"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "./drawer"
import { cn } from "../../lib/utils"
import { useIsMobile } from "../../lib/use-mobile"
import { PeriodPreset, resolvePeriodRange } from "../../lib/period-range"

/**
 * The default finance date-range presets: current / last / to-date for month, quarter, and financial year —
 * three groups of three. Callers can supply their own `presetGroups` (e.g. the people/members analytics use
 * rolling windows) to reuse this same control everywhere.
 */
const DEFAULT_PERIOD_GROUPS: ReadonlyArray<readonly PeriodPreset[]> = [
  [PeriodPreset.ThisMonth, PeriodPreset.ThisQuarter, PeriodPreset.ThisYear],
  [PeriodPreset.LastMonth, PeriodPreset.LastQuarter, PeriodPreset.LastYear],
  [PeriodPreset.MonthToDate, PeriodPreset.QuarterToDate, PeriodPreset.YearToDate],
]

export type PeriodPickerLabels = {
  /** The "Period:" prefix on the trigger. */
  label: string
  /** Label for the custom-range item and trigger. */
  custom: string
  /** One label per preset, keyed by `PeriodPreset`. */
  presets: Partial<Record<PeriodPreset, string>>
}

type PeriodPickerProps = {
  /**
   * Chrome strings, supplied by the host. The picker used to call the tenant's `useTranslations` directly,
   * which is what kept it in one app despite documenting itself as reusable.
   */
  labels: PeriodPickerLabels
  /**
   * IANA zone the ranges resolve in. Injected because it is a tenant fact (the workspace's timezone) that a
   * shared control must not go and fetch for itself — the admin app has no workspace to ask.
   */
  timeZone: string

  value: PeriodPreset
  onValueChange: (preset: PeriodPreset) => void
  /** Preset groups to offer (each inner array is a separated group). Defaults to the finance presets. */
  presetGroups?: ReadonlyArray<readonly PeriodPreset[]>
  className?: string
  /** Opt-in custom-range mode: adds a "Custom range" item. The caller owns the range state + its picker UI. */
  allowCustom?: boolean
  /** True while the caller is in custom mode — the trigger shows `customLabel` and the item reads as selected. */
  isCustom?: boolean
  /** Trigger label when `isCustom` (e.g. the chosen "1 Jul – 30 Sep 2026" range). */
  customLabel?: string
  onCustomSelect?: () => void
  /** Drop the "Period:" prefix from the trigger, for a compact chip where the context already says it. */
  hideLabel?: boolean
}

/** Compact human label for a resolved range: a whole calendar month → "Jul 2026"; else "1 Jul – 30 Sep 2026". */
function formatRangeLabel(start: Date, end: Date): string {
  const wholeMonth =
    isFirstDayOfMonth(start) &&
    isLastDayOfMonth(end) &&
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth()
  if (wholeMonth) return format(start, "MMM yyyy")

  const sameYear = start.getFullYear() === end.getFullYear()
  return `${format(start, sameYear ? "d MMM" : "d MMM yyyy")} – ${format(end, "d MMM yyyy")}`
}

/**
 * The standard reporting period picker: a compact trigger showing the current preset, opening a grouped
 * preset list where each row shows its resolved date range (computed in the workspace timezone). Optionally
 * offers a caller-managed custom range.
 */
export function PeriodPicker({
  labels,
  timeZone,
  value,
  onValueChange,
  presetGroups = DEFAULT_PERIOD_GROUPS,
  className,
  allowCustom = false,
  isCustom = false,
  customLabel,
  onCustomSelect,
  hideLabel = false,
}: PeriodPickerProps) {

  const ranges = React.useMemo(() => {
    const zonedNow = toZonedTime(new Date(), timeZone)
    const map = new Map<PeriodPreset, { start: Date; end: Date }>()
    for (const preset of presetGroups.flat()) {
      const range = resolvePeriodRange(preset, { now: zonedNow, weekStartsOn: 1 })
      if (range) map.set(preset, range)
    }
    return map
  }, [presetGroups, timeZone])

  const isMobile = useIsMobile()
  const [sheetOpen, setSheetOpen] = React.useState(false)
  const triggerLabel = isCustom ? (customLabel ?? labels.custom) : (labels.presets[value] ?? value)

  const trigger = (
    <Button variant="outline" size="sm" className={cn("gap-1.5", className)}>
      {hideLabel ? null : <span className="text-muted-foreground">{labels.label}:</span>}
      <span className="font-medium">{triggerLabel}</span>
      <ChevronDownIcon className="size-4 opacity-60" />
    </Button>
  )

  // On a phone the same groups open as a bottom sheet: full-width rows a thumb can hit, not a small dropdown.
  if (isMobile) {
    const pick = (action: () => void) => {
      setSheetOpen(false)
      action()
    }
    const rowClassName =
      "flex min-h-12 w-full items-center justify-between gap-4 px-3 text-left text-[15px] transition-colors active:bg-muted focus-visible:outline-none focus-visible:bg-muted"
    return (
      <Drawer open={sheetOpen} onOpenChange={setSheetOpen}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent>
          <DrawerHeader className="group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left">
            <DrawerTitle className="text-lg font-bold">{labels.label}</DrawerTitle>
            <DrawerDescription className="sr-only">{triggerLabel}</DrawerDescription>
          </DrawerHeader>
          <div className="flex flex-col gap-3 overflow-y-auto overscroll-contain [&>*]:shrink-0 px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
            {presetGroups.map((group, index) => (
              <ul key={index} className="divide-border/60 bg-muted/60 divide-y overflow-hidden rounded-2xl">
                {group.map((preset) => {
                  const range = ranges.get(preset)
                  const selected = !isCustom && preset === value
                  return (
                    <li key={preset}>
                      <button
                        type="button"
                        aria-current={selected ? "true" : undefined}
                        onClick={() => pick(() => onValueChange(preset))}
                        className={cn(rowClassName, selected ? "text-primary font-bold" : "text-foreground font-medium")}
                      >
                        <span className="min-w-0 truncate">{labels.presets[preset] ?? preset}</span>
                        <span className="flex shrink-0 items-center gap-2">
                          {range ? (
                            <span
                              className={cn(
                                "text-xs font-medium tabular-nums",
                                selected ? "text-primary/80" : "text-muted-foreground",
                              )}
                            >
                              {formatRangeLabel(range.start, range.end)}
                            </span>
                          ) : null}
                          {selected ? <CheckIcon aria-hidden className="size-4" /> : null}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            ))}
            {allowCustom ? (
              <ul className="bg-muted/60 overflow-hidden rounded-2xl">
                <li>
                  <button
                    type="button"
                    aria-current={isCustom ? "true" : undefined}
                    onClick={() => pick(() => onCustomSelect?.())}
                    className={cn(rowClassName, isCustom ? "text-primary font-bold" : "text-foreground font-medium")}
                  >
                    <span className="min-w-0 truncate">{labels.custom}</span>
                    {isCustom ? <CheckIcon aria-hidden className="size-4 shrink-0" /> : null}
                  </button>
                </li>
              </ul>
            ) : null}
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        {presetGroups.map((group, index) => (
          <React.Fragment key={index}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            {group.map((preset) => {
              const range = ranges.get(preset)
              const selected = !isCustom && preset === value
              return (
                <DropdownMenuItem
                  key={preset}
                  onSelect={() => onValueChange(preset)}
                  className={cn("justify-between gap-6", selected && "text-primary")}
                >
                  <span className={cn(selected && "font-semibold")}>{labels.presets[preset] ?? preset}</span>
                  {range ? (
                    <span
                      className={cn("text-xs tabular-nums", selected ? "text-primary/80" : "text-muted-foreground")}
                    >
                      {formatRangeLabel(range.start, range.end)}
                    </span>
                  ) : null}
                </DropdownMenuItem>
              )
            })}
          </React.Fragment>
        ))}
        {allowCustom ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => onCustomSelect?.()}
              className={cn("justify-between gap-6", isCustom && "text-primary")}
            >
              <span className={cn(isCustom && "font-semibold")}>{labels.custom}</span>
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
